import { useMemo, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Search, X } from "lucide-react";
import PageHero from "@/components/PageHero";
import ProductCard from "@/components/ProductCard";
import ProductModal from "@/components/ProductModal";
import PageLinkCTA from "@/components/PageLinkCTA";
import { CATEGORIES, PRODUCTS } from "@/data/products";

const FILTERS = ["All", ...CATEGORIES, "Coming Soon"];

export default function Products() {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("All");
  const [modalProduct, setModalProduct] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return PRODUCTS.filter((p) => {
      const matchesFilter =
        filter === "All" ||
        (filter === "Coming Soon" ? p.comingSoon : p.category === filter);
      const matchesQuery =
        !q || p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q);
      return matchesFilter && matchesQuery;
    });
  }, [query, filter]);

  return (
    <>
      <PageHero
        eyebrow="Products"
        title="Railway Component Catalogue"
        description="Railway electrical, electromechanical, lighting, traction-support and pipe fitting products — manufactured and supplied with a quality-first approach."
      />

      <section className="relative bg-[#F3F7FB] pb-24" data-testid="products-section">
        <div className="wrap relative">
          {/* Search + filters */}
          <div className="sticky top-[76px] z-30 -mx-2 border-b border-white/[0.06] bg-white/85 px-2 pb-5 pt-5 backdrop-blur-xl">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="relative w-full max-w-md">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search products…"
                  aria-label="Search products"
                  data-testid="product-search-input"
                  className="field !pl-11 !pr-10"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    aria-label="Clear search"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-ink"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500" data-testid="products-count">
                {filtered.length} / {PRODUCTS.length} products
              </p>
            </div>
            <div className="mt-4 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  type="button"
                  data-testid="product-category-filter"
                  data-active={filter === f}
                  onClick={() => setFilter(f)}
                  className={`whitespace-nowrap rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.16em] transition-all duration-300 ${
                    filter === f
                      ? "border-brand-emerald bg-brand-emerald text-navy-950 shadow-[0_0_24px_rgba(16,185,129,0.35)]"
                      : "border-navy-900/15 text-slate-500 hover:border-brand-emeraldLight/60 hover:text-brand-emeraldDark"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          {/* Grid */}
          {filtered.length > 0 ? (
            <div className="mt-10 grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
              <AnimatePresence mode="popLayout">
                {filtered.map((p, i) => (
                  <ProductCard key={p.id} product={p} index={i} onOpen={setModalProduct} />
                ))}
              </AnimatePresence>
            </div>
          ) : (
            <div className="mt-16 flex flex-col items-center gap-5 py-16 text-center" data-testid="products-empty-state">
              <Search className="h-10 w-10 text-slate-700" />
              <p className="text-slate-500">No products match your search.</p>
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  setFilter("All");
                }}
                className="btn btn-outline"
                data-testid="products-reset-filters"
              >
                Reset Search &amp; Filters
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="wrap pb-24" data-testid="products-cta">
        <PageLinkCTA />
      </section>

      <ProductModal product={modalProduct} onClose={() => setModalProduct(null)} />
    </>
  );
}
